import { PlatformChartMomentExportDto } from '@data/_generated-api/platform-api/models/platform-chart-moment-export-dto';
import { PlatformChartNoteAttachmentDto } from '@data/_generated-api/platform-api/models/platform-chart-note-attachment-dto';
import {
  fileNamePart,
  mdFrontmatter,
  mdInline,
  mdJoinBlocks,
  mdLinkTarget,
  mdSlug,
  mdTable,
  safeFileName,
} from '@shared/utils/markdown-export.utils';
import { supportMarkupToMarkdown } from '@/app/support/support-markup';
import { MdPlanetLabels, MdPlanetRow, planetsMarkdown } from '../../export/hd-export-planets.markdown';
import { hdMomentColorHex, parseHdMomentEmotions } from '../bodygraph/advanced-properties/hd-moment-fields';
import { HdMindMomentExtra, hdMindConditionerText, parseHdMindMomentExtra } from '../bodygraph/advanced-properties/hd-moment-extra';

/** Alle vaste teksten van een moment-.md; de service vertaalt ze in de content-taal. */
export interface HdMomentMarkdownLabels {
  readonly chartType: (type: number) => string;
  readonly category: (key: string) => string;
  readonly mood: (value: number) => string;
  readonly vitality: (value: number) => string;
  readonly emotion: (key: string) => string;
  readonly moodTitle: string;
  readonly vitalityTitle: string;
  readonly emotionsTitle: string;
  /** Mind-notitie (spec 2026-10-22-mind-logboek §6): kop, centrumnaam, poortlabel en planeetnaam. */
  readonly mindTitle: string;
  readonly centerName: (center: string) => string;
  readonly gateLabel: (gate: number) => string;
  readonly planetName: (planetKey: string) => string;
  /** Kop per link, bv. "Moment van Steven" of "Cycle van Steven". */
  readonly linkTitle: (relationType: string, chartName: string) => string;
  readonly notes: string;
  readonly planetsAt: string;
  readonly planetsAtStart: string;
  readonly planets: MdPlanetLabels;
  readonly until: string;
  readonly exported: (date: string) => string;
}

/** Waar een bijlage in de zip staat; ontbreekt hij (laadfout), dan blijft enkel de naam staan. */
export type HdMomentAttachmentPaths = ReadonlyMap<string, string>;

export interface HdMomentMarkdownInput {
  readonly moment: PlatformChartMomentExportDto;
  readonly planets: readonly MdPlanetRow[];
  readonly attachmentPaths: HdMomentAttachmentPaths;
  /** Lokaal opgemaakt begin en (bij een periode) einde. */
  readonly startText?: string;
  readonly endText?: string;
  /** ISO met lokale offset (frontmatter). */
  readonly startIso?: string;
  readonly endIso?: string;
  readonly exportedText: string;
  readonly exportedIso: string;
  readonly labels: HdMomentMarkdownLabels;
}

/** Het tijdstip waarop een moment staat: zelfde volgorde als de Momenten-lijst. */
export function momentStartUtc(moment: PlatformChartMomentExportDto): string | undefined {
  return moment.momentUtc ?? moment.cycleAnchorUtc ?? moment.birthDateUtc ?? undefined;
}

/**
 * Cycles horen alleen in de export wanneer er iets over geschreven is (beslissing 2026-10-21):
 * een eigen notitie, een linknotitie of een bijlage. Gelogde momenten gaan altijd mee.
 */
export function shouldExportMoment(moment: PlatformChartMomentExportDto): boolean {
  const isCycle = (moment.links ?? []).length > 0 && (moment.links ?? []).every(link => link.relationType === 'cycleOf');
  if (!isCycle) return true;
  if (moment.generalNotes?.trim() || (moment.noteAttachments ?? []).length > 0) return true;
  return (moment.links ?? []).some(link => link.note?.trim() || (link.noteAttachments ?? []).length > 0);
}

/**
 * Eén moment als Markdown (spec 2026-10-21-dagboek-momenten-export-design §2.3): frontmatter,
 * titel, datum of periode, stemming/vitaliteit/emoties, per link de chart waar het moment bij
 * hoort met de linknotitie, de eigen notitie, afbeeldingen en de planeetstanden. Puur.
 */
export function buildMomentMarkdown(input: HdMomentMarkdownInput): string {
  const { moment, labels } = input;
  const links = moment.links ?? [];
  const emotions = parseHdMomentEmotions(moment.emotions);
  const isCycle = links.length > 0 && links.every(link => link.relationType === 'cycleOf');
  const mind = parseHdMindMomentExtra(moment.momentExtraJson);
  const mindParts = mind ? mindTextParts(mind, labels) : [];

  const frontmatter = mdFrontmatter({
    type: isCycle ? 'cycle' : 'moment',
    title: moment.name ?? '',
    start: input.startIso,
    end: input.endIso,
    category: moment.category ?? undefined,
    mood: moment.mood ?? undefined,
    mood_label: moment.mood != null ? labels.mood(moment.mood) : undefined,
    vitality: moment.vitality ?? undefined,
    vitality_label: moment.vitality != null ? labels.vitality(moment.vitality) : undefined,
    emotions: emotions.map(key => labels.emotion(key)),
    mind_center: mind?.center ? labels.centerName(mind.center) : undefined,
    mind_gate: mind?.gate ?? undefined,
    conditioners: mind ? conditionerTexts(mind, labels) : [],
    moment_of: links.map(link => link.otherChartName ?? '').filter(name => name.length > 0),
    chart_type: labels.chartType(moment.type ?? 0),
    cycle: moment.cycleKey ?? undefined,
    color: hdMomentColorHex(moment.color) ?? undefined,
    exported: input.exportedIso,
  });

  const when = input.endText ? `${input.startText} ${labels.until} ${input.endText}` : input.startText;
  const meta = [
    when ? `*${mdInline(when)}*` : '',
    moment.category ? labels.category(moment.category) : '',
    moment.type != null ? labels.chartType(moment.type) : '',
  ]
    .filter(part => part.length > 0)
    .join(' · ');

  return `${mdJoinBlocks([
    frontmatter,
    `# ${mdInline(moment.name) || '?'}`,
    meta,
    mindParts.length > 0 ? `**${mdInline(labels.mindTitle)}:** ${mindParts.map(part => mdInline(part)).join(' · ')}` : '',
    detailsTable(moment, emotions, labels),
    ...links.map(link =>
      mdJoinBlocks([
        `## ${mdInline(labels.linkTitle(link.relationType ?? '', link.otherChartName ?? ''))}`,
        `*${labels.chartType(link.otherChartType ?? 0)}*`,
        supportMarkupToMarkdown(link.note),
        attachmentsMarkdown(link.noteAttachments, input.attachmentPaths),
      ]),
    ),
    moment.generalNotes?.trim() || (moment.noteAttachments ?? []).length > 0
      ? mdJoinBlocks([`## ${labels.notes}`, supportMarkupToMarkdown(moment.generalNotes), attachmentsMarkdown(moment.noteAttachments, input.attachmentPaths)])
      : '',
    input.planets.length > 0
      ? `## ${input.endText ? labels.planetsAtStart : labels.planetsAt}\n\n${planetsMarkdown(input.planets, labels.planets)}`
      : '',
    `---\n\n*${labels.exported(input.exportedText)}*`,
  ])}\n`;
}

/** Hoeveel tekens van de momentnaam in de bestandsnaam komen (ingekort op een woordgrens). */
const FILE_NAME_MOMENT_MAX = 70;

/** Bestandsnaam: `2026-10-21 Verhuis naar Gent.md`; de datum blijft altijd volledig. */
export function momentFileName(moment: PlatformChartMomentExportDto, localDate: string | undefined): string {
  return safeFileName(`${localDate ?? ''} ${fileNamePart(moment.name, FILE_NAME_MOMENT_MAX)}`.trim(), 'md');
}

/** Pad van een bijlage in de zip: `attachments/2026-10-21-verhuis-naar-gent-1.jpg`. */
export function attachmentPath(moment: PlatformChartMomentExportDto, localDate: string | undefined, index: number, attachment: PlatformChartNoteAttachmentDto): string {
  const extension = attachment.contentType === 'image/png' ? 'png' : 'jpg';
  return `attachments/${[localDate, mdSlug(moment.name ?? '')].filter(Boolean).join('-')}-${index}.${extension}`;
}

export interface HdMomentIndexEntry {
  readonly fileName: string;
  readonly dateText: string;
  readonly name: string;
  readonly category: string;
}

export interface HdMomentIndexLabels {
  readonly title: string;
  readonly summary: string;
  readonly date: string;
  readonly name: string;
  readonly category: string;
}

/** `index.md` van een momentenexport: één tabelrij per moment, met link. */
export function buildMomentsIndexMarkdown(entries: readonly HdMomentIndexEntry[], labels: HdMomentIndexLabels): string {
  const table = mdTable(
    [labels.date, labels.name, labels.category],
    entries.map(entry => [entry.dateText, `[${entry.name}](${mdLinkTarget(entry.fileName)})`, entry.category]),
  );
  return `${mdJoinBlocks([`# ${mdInline(labels.title)}`, `*${labels.summary}*`, table])}\n`;
}

function detailsTable(moment: PlatformChartMomentExportDto, emotions: readonly string[], labels: HdMomentMarkdownLabels): string {
  const headers: string[] = [];
  const cells: string[] = [];
  if (moment.mood != null) {
    headers.push(labels.moodTitle);
    cells.push(`${labels.mood(moment.mood)} (${moment.mood}/5)`);
  }
  if (moment.vitality != null) {
    headers.push(labels.vitalityTitle);
    cells.push(`${labels.vitality(moment.vitality)} (${moment.vitality}/5)`);
  }
  if (emotions.length > 0) {
    headers.push(labels.emotionsTitle);
    cells.push(emotions.map(key => labels.emotion(key)).join(', '));
  }
  return headers.length > 0 ? mdTable(headers, [cells]) : '';
}

function attachmentsMarkdown(attachments: readonly PlatformChartNoteAttachmentDto[] | null | undefined, paths: HdMomentAttachmentPaths): string {
  return (attachments ?? [])
    .map(attachment => {
      const name = mdInline(attachment.fileName ?? '').replace(/[[\]]/g, '');
      const path = attachment.id ? paths.get(attachment.id) : undefined;
      return path ? `![${name}](${mdLinkTarget(path)})` : `*${name}*`;
    })
    .join('\n\n');
}

/** Mind-regel: centrum, poort en conditioneerders, enkel wat bewaard is. */
function mindTextParts(mind: HdMindMomentExtra, labels: HdMomentMarkdownLabels): string[] {
  const parts: string[] = [];
  if (mind.center) parts.push(labels.centerName(mind.center));
  if (mind.gate != null) parts.push(labels.gateLabel(mind.gate));
  return [...parts, ...conditionerTexts(mind, labels)];
}

/** Per conditioneerder "Sun 64.3" of "Ann (21, 40)"; leeg zonder conditioneerders. */
function conditionerTexts(mind: HdMindMomentExtra, labels: HdMomentMarkdownLabels): string[] {
  return (mind.conditioners ?? []).map(conditioner => hdMindConditionerText(conditioner, labels.planetName));
}
